import {
  useDeletePortfolioMediaMutation,
  useUpdatePortfolioMediaMutation,
} from "app/services/api/residentsApi";
import { useResidentContext } from "../provider/ResidentProvider";
import { useState } from "react";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import { useForm } from "react-hook-form";
import { convertToFile } from "ui/utils/fileUtils";
import { handleBackendError } from "ui/utils/Errors";
import { AddResidentPortfolioForm } from "app/services/api/types/residents/models/AddResidentPortfolioForm";
import { DocumentPickerAsset } from "expo-document-picker";
import { usePageFocus } from "ui/utils/FocusHook";
import { isDocumentPickerAsset, isMediaModel } from "ui/types/ThrelsFileType";

export const useHandleResidentPortfolio = () => {
  const { resident } = useResidentContext();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { showNewToast, showErrorToast } = useDisplayToast();

  const [triggerUpdatePortfolioMedia] = useUpdatePortfolioMediaMutation();
  const [triggerDeletePortfolioMedia] = useDeletePortfolioMediaMutation();

  const {
    control,
    reset,
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = useForm<AddResidentPortfolioForm>({
    defaultValues: {
      portfolio_medias: [],
    },
  });

  usePageFocus(() => {
    if (resident) {
      reset({
        portfolio_medias: resident.portfolio_medias || [],
      });
    }
  }, [resident, reset]);

  const updatePortfolio = async (
    portfolioMedias: DocumentPickerAsset[],
    residentId: number,
  ) => {
    const formData = new FormData();
    portfolioMedias.forEach((file) => {
      const convertedFiles: File[] = convertToFile([file]);
      convertedFiles.forEach((convertedFile) => {
        formData.append("portfolio_medias[]", convertedFile);
      });
    });

    await triggerUpdatePortfolioMedia({ residentId, body: formData }).unwrap();
  };

  const submitForm = async () => {
    return new Promise<boolean>((resolve) => {
      if (isSubmitting) {
        resolve(false);
        return;
      }
      handleSubmit(
        async ({ portfolio_medias }) => {
          try {
            const residentId = resident?.id;
            if (!residentId) {
              resolve(false);
              return;
            }
            const portfolioMedias = portfolio_medias?.filter((asset) =>
              isDocumentPickerAsset(asset),
            );
            const portfolioDeletedMedia = resident?.portfolio_medias?.filter(
              (media) =>
                !portfolio_medias?.some(
                  (item) => isMediaModel(item) && item.id === media.id,
                ),
            );

            if (portfolioMedias?.length)
              await updatePortfolio(portfolioMedias, residentId);

            if (portfolioDeletedMedia?.length) {
              await Promise.all(
                portfolioDeletedMedia.map((media) =>
                  triggerDeletePortfolioMedia({
                    residentId,
                    mediaId: media.id,
                  }).unwrap(),
                ),
              );
            }

            showNewToast({
              title: "Resident Portfolio Updated",
              message: "Successfully updated resident's portfolio.",
              type: "success",
            });

            resolve(true);
          } catch (e) {
            const result = handleBackendError(e, setError, setErrorMessage);
            showErrorToast(e);
            resolve(result);
          }
        },
        (errors) => {
          console.error("Form errors:", errors);
          resolve(false);
        },
      )();
    });
  };

  return {
    control,
    errorMessage,
    submitForm,
  };
};
